/* ============================================================
   PDP BUNDLE WIDGET V1 - add-to-cart handler
   --------------------------------------------------------------
   Listens for clicks on [data-bundle-add] inside the bundle widget.
   On click:
     1. Reads the current anchor variant ID from the live PDP form's
        hidden input (the variant ID updates as the user changes options).
     2. Reads the companion variant ID from the button's data-attr
        (server-rendered by the snippet at request time).
     3. POSTs to /cart/add.js with both items in a single request.
     4. On success, dispatches `theme:cart:popdown` on the document
        (matches the existing V3 pattern at snippets/lh-cart-notification.liquid).
        V3 has no cart drawer; the LH cart-notification bar is the
        existing UX surface for "added to cart" feedback.
     5. On error, surfaces the message inline in the card.
============================================================ */
(function () {
  'use strict';

  var CART_ADD_URL = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';

  function showError(card, message) {
    var errEl = card && card.querySelector('[data-bundle-error]');
    if (!errEl) return;
    errEl.textContent = message || 'Could not add to cart. Please try again.';
  }

  function clearError(card) {
    var errEl = card && card.querySelector('[data-bundle-error]');
    if (errEl) errEl.textContent = '';
  }

  function setLoading(button, isLoading) {
    if (isLoading) {
      button.setAttribute('data-state', 'loading');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('data-state');
      button.removeAttribute('aria-busy');
    }
  }

  function dispatchCartPopdown(companionTitle) {
    /* V3 cart-notification bar (snippets/lh-cart-notification.liquid)
       reads event.detail.variant.product_title or .title to build its
       message. We pass the companion title so the bar shows the upsell
       product the customer just added. The host PDP product is never
       added through this path, so the popdown only ever announces the
       companion. */
    var ev = new CustomEvent('theme:cart:popdown', {
      detail: { variant: { product_title: companionTitle, title: companionTitle } },
      bubbles: true
    });
    document.dispatchEvent(ev);
  }

  function addBundle(button) {
    var card = button.closest('[data-bundle-card]');
    var companionId = parseInt(button.getAttribute('data-companion-variant-id'), 10);
    var companionTitle = button.getAttribute('data-companion-title') || 'Item';

    clearError(card);

    if (!companionId) {
      showError(card, 'Could not resolve companion product.');
      return;
    }

    setLoading(button, true);

    /* Upsell path: add the companion only. The host PDP product is never
       auto-added here. A variant-required host (e.g. the R&R kit) can only
       enter the cart through its own validated product form. */
    var payload = {
      items: [
        { id: companionId, quantity: 1 }
      ]
    };

    fetch(CART_ADD_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .then(function (result) {
        setLoading(button, false);
        if (!result.ok) {
          /* Shopify returns { description, message, status } on 4xx */
          var msg = (result.data && (result.data.description || result.data.message)) || 'Could not add to cart.';
          showError(card, msg);
          return;
        }
        dispatchCartPopdown(companionTitle);
      })
      .catch(function () {
        setLoading(button, false);
        showError(card, 'Network error - please try again.');
      });
  }

  function onClick(e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-bundle-add]');
    if (!btn) return;
    e.preventDefault();
    addBundle(btn);
  }

  /* ============================================================
     MODE A horizontal alignment to ATC button (Pass 17c)
     --------------------------------------------------------------
     CSS-only centering of the Mode A card couldn't match the ATC
     button's horizontal center because Pipeline's wrapper chain
     introduces padding we can't statically match. This measures
     both elements at runtime and sets inline margin-left on the
     Mode A card so its center aligns with the ATC's center.
     Re-runs on window resize (debounced) so alignment holds
     across viewport changes.
  ============================================================ */
  function alignModeAToATC() {
    var atc = document.querySelector('[data-add-to-cart]') ||
              document.querySelector('button.btn--add-to-cart') ||
              document.querySelector('button[name="add"]');
    var modeA = document.querySelector('.lh-bundle-widget--upsell');
    if (!atc || !modeA) return;

    /* Reset any previous offset so we measure the natural position. */
    modeA.style.marginLeft = '0px';
    modeA.style.marginRight = '0px';

    var atcRect = atc.getBoundingClientRect();
    var modeARect = modeA.getBoundingClientRect();
    if (atcRect.width === 0 || modeARect.width === 0) return;

    var atcCenter = atcRect.left + atcRect.width / 2;
    var modeADesiredLeft = atcCenter - modeARect.width / 2;
    var offset = modeADesiredLeft - modeARect.left;

    modeA.style.marginLeft = offset + 'px';
  }

  var resizeAlignTimeout;
  function scheduleAlign() {
    clearTimeout(resizeAlignTimeout);
    resizeAlignTimeout = setTimeout(alignModeAToATC, 100);
  }

  /* ============================================================
     Reactive accessories-kit size matching
     --------------------------------------------------------------
     When the customer changes the native size variant on an anchor
     PDP, swap the Care and Repair Accessories Kit upsell to the
     size-matched variant, keeping the category fixed. Updates both:
       (i)  the PDP bundle card (companion variant id, price, the
            contents sentence, image), and
       (ii) window.LH_BUNDLE_CONTEXT.slots so the cart drawer, which
            reads that global live on open, inherits the same variant.

     The size rule itself lives in Liquid (snippets/lh-acc-size-token
     .liquid) and is baked here as ctx.sizeByVariant, so this file
     never re-implements it. An unmapped variant leaves the current
     selection untouched rather than defaulting to Small.
  ============================================================ */
  function initAccessoriesSync() {
    var ctx = window.LH_BUNDLE_CONTEXT;
    if (!ctx || ctx.mode !== 'standard') return;
    if (!ctx.sizeByVariant || !ctx.accessoriesVariants || !ctx.accessoriesVariants.length || !ctx.category) return;

    /* Build category -> size -> accessories variant lookup. Keys are lowercased
       so they agree with ctx.category, which Liquid emits as 'repair'/'care'. */
    var accLookup = {};
    ctx.accessoriesVariants.forEach(function (v) {
      var catKey = String(v.category).toLowerCase();
      if (!accLookup[catKey]) accLookup[catKey] = {};
      accLookup[catKey][v.size] = v;
    });

    var card = document.querySelector('[data-companion-handle="' + ctx.accessoriesSlotHandle + '"]');

    /* Index of the accessories slot inside ctx.slots (drawer source). */
    var accSlotIdx = -1;
    if (ctx.slots) {
      for (var i = 0; i < ctx.slots.length; i++) {
        if (ctx.slots[i] && ctx.slots[i].handle === ctx.accessoriesSlotHandle) { accSlotIdx = i; break; }
      }
    }

    function applyForVariant(variantId) {
      var token = ctx.sizeByVariant[String(variantId)];
      if (!token) return; /* unmapped anchor variant: leave current selection */
      var catKey = String(ctx.category).toLowerCase();
      var acc = accLookup[catKey] && accLookup[catKey][token];
      if (!acc) return;

      /* (i) PDP bundle card */
      if (card) {
        var btn = card.querySelector('[data-bundle-add]');
        if (btn) {
          btn.setAttribute('data-companion-variant-id', acc.variantId);
          var label = card.querySelector('.lh-bundle-card-cta-label');
          if (label) {
            var base = btn.getAttribute('data-cta-base') || 'Add accessories kit';
            label.textContent = base + ' • ' + acc.priceFormatted;
          }
        }
        var summary = card.querySelector('[data-acc-summary]');
        if (summary) {
          var txt = card.getAttribute(token === 'Large' ? 'data-acc-summary-large' : 'data-acc-summary-small');
          if (txt) summary.textContent = txt;
        }
        var img = card.querySelector('.lh-bundle-card-img img');
        if (img && acc.img) img.setAttribute('src', acc.img);
      }

      /* (ii) cart drawer context (read live by lh-cart-drawer.js on open) */
      if (accSlotIdx > -1) {
        var slot = ctx.slots[accSlotIdx];
        slot.variantId = acc.variantId;
        slot.priceCents = acc.priceCents;
        slot.priceFormatted = acc.priceFormatted;
        slot.img = acc.img;
        slot.available = acc.available;
      }
    }

    /* Initial sync from the live form selection, so a ?variant= deep link
       and the server render agree with no flash (idempotent on a clean load). */
    var form = document.querySelector('form[data-product-form]');
    var idInput = form && form.querySelector('input[name="id"]');
    if (idInput && idInput.value) applyForVariant(idInput.value);

    /* React to native variant changes, debounced. */
    var debounceTimer;
    document.addEventListener('theme:variant:change', function (e) {
      if (!e.detail || !e.detail.variant) return; /* null when combo unavailable */
      var vid = e.detail.variant.id;
      var logToken = ctx.sizeByVariant[String(vid)];
      var logCatKey = String(ctx.category).toLowerCase();
      var logAcc = logToken && accLookup[logCatKey] && accLookup[logCatKey][logToken];
      console.log('LH_ACC_SYNC', { variantId: vid, sizeToken: logToken, accessoriesVariantId: logAcc ? logAcc.variantId : undefined });
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { applyForVariant(vid); }, 200);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      document.addEventListener('click', onClick);
      alignModeAToATC();
      initAccessoriesSync();
    });
  } else {
    document.addEventListener('click', onClick);
    alignModeAToATC();
    initAccessoriesSync();
  }

  /* Run once more after window load in case images / fonts shift the ATC's
     final position after DOMContentLoaded but before paint stabilises. */
  window.addEventListener('load', alignModeAToATC);
  window.addEventListener('resize', scheduleAlign);
})();
